import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SecurityIcon from '@mui/icons-material/Security';
import PsychologyIcon from '@mui/icons-material/Psychology';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RocketIcon from '@mui/icons-material/Rocket';
import TargetIcon from '@mui/icons-material/MyLocation';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import FolderIcon from '@mui/icons-material/Folder';
import SettingsIcon from '@mui/icons-material/Settings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import LockIcon from '@mui/icons-material/Lock';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import DomainIcon from '@mui/icons-material/Domain';
import BusinessIcon from '@mui/icons-material/Business';
import PublicIcon from '@mui/icons-material/Public';
import BuildIcon from '@mui/icons-material/Build';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import CodeIcon from '@mui/icons-material/Code';
import PaletteIcon from '@mui/icons-material/Palette';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import UpdateIcon from '@mui/icons-material/Update';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import SearchIcon from '@mui/icons-material/Search';
import BarChartIcon from '@mui/icons-material/BarChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PowerIcon from '@mui/icons-material/Power';
import ApiIcon from '@mui/icons-material/Api';
import WebhookIcon from '@mui/icons-material/Webhook';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import ArticleIcon from '@mui/icons-material/Article';
import AssignmentIcon from '@mui/icons-material/Assignment';
import '../styles/Why.css';

const Why: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box className="why-page" sx={{ bgcolor: 'transparent', minHeight: '100vh' }}>
      <Container maxWidth="lg">
        {/* Section 1: Hero Section with Badges */}
        <Box sx={{ py: { xs: 6, md: 10 } }}>
          <Box className="why-hero">
          {/* Badges */}
          <Box className="why-hero-badges" sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4, flexWrap: 'wrap' }}>
            <Chip
              className="why-chip-ai"
              icon={<AutoAwesomeIcon sx={{ color: 'white !important', fontSize: '1.1rem' }} />}
              label="AI-POWERED"
              sx={{
                background: 'linear-gradient(135deg, #10b981 0%, #14b8a6 45%, #22d3ee 100%)',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.875rem',
                height: '40px',
                px: 1,
                '& .MuiChip-icon': {
                  color: 'white',
                },
              }}
            />
            <Chip
              label="ENTERPRISE-GRADE"
              variant="outlined"
              sx={{
                borderColor: '#10b981',
                borderWidth: 2,
                color: '#10b981',
                fontWeight: 'bold',
                fontSize: '0.875rem',
                height: '40px',
                px: 1,
              }}
            />
          </Box>

          {/* Main Heading */}
          <Typography
            className="why-hero-title"
            variant="h2"
            component="h1"
            align="center"
            sx={{
              fontWeight: 700,
              mb: 4,
              fontSize: { xs: '2.5rem', md: '3.75rem' },
            }}
          >
            Why Choose CRUD Expert?
          </Typography>

          {/* Description */}
          <Typography
            className="why-hero-desc"
            variant="h6"
            align="center"
            sx={{
              maxWidth: '1000px',
              mx: 'auto',
              mb: 8,
              color: '#4b5563',
              lineHeight: 1.8,
              fontSize: { xs: '1rem', md: '1.25rem' },
            }}
          >
            CRUD Expert is an AI-powered, enterprise-grade API testing and automation platform 
            designed for modern development teams. Built with cutting-edge AI technology and a 
            focus on user experience, CRUD Expert leverages artificial intelligence to revolutionize 
            how you test, validate, and automate your APIs. Our platform combines intelligent automation, 
            comprehensive testing capabilities, and seamless collaboration tools to accelerate your 
            API development lifecycle.
          </Typography>
          </Box>

          {/* Feature Cards - 2x2 Grid */}
          <Grid container spacing={4} sx={{ mb: 8 }}>
            <Grid item xs={12} sm={6} md={6}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid #e5e7eb',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      backgroundColor: '#10b981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <SmartToyIcon sx={{ color: 'white', fontSize: 32 }} />
                  </Box>
                  <Typography
                    variant="h4"
                    component="h2"
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}
                  >
                    AI at the Core
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: '#6b7280', lineHeight: 1.7, fontSize: '1.1rem' }}
                  >
                    Advanced AI that suggests data sets, generates test cases, and recommends 
                    assertions automatically
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid #e5e7eb',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      backgroundColor: '#10b981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <RocketLaunchIcon sx={{ color: 'white', fontSize: 32 }} />
                  </Box>
                  <Typography
                    variant="h4"
                    component="h2"
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}
                  >
                    Comprehensive Features
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: '#6b7280', lineHeight: 1.7, fontSize: '1.1rem' }}
                  >
                    Everything you need from request building to automated test cycles and team 
                    collaboration
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid #e5e7eb',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      backgroundColor: '#10b981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <SecurityIcon sx={{ color: 'white', fontSize: 32 }} />
                  </Box>
                  <Typography
                    variant="h4"
                    component="h2"
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}
                  >
                    Enterprise Security
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: '#6b7280', lineHeight: 1.7, fontSize: '1.1rem' }}
                  >
                    Multi-tenancy, data isolation, and compliance features built for enterprise needs
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid #e5e7eb',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      backgroundColor: '#10b981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <IntegrationInstructionsIcon sx={{ color: 'white', fontSize: 32 }} />
                  </Box>
                  <Typography
                    variant="h4"
                    component="h2"
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}
                  >
                    Swagger Integration
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: '#6b7280', lineHeight: 1.7, fontSize: '1.1rem' }}
                  >
                    Import complete API specifications, auto-generate collections, and preserve schemas for validation
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 8 }} />

        {/* Section 2: AI-Powered Intelligence */}
        <Box sx={{ py: { xs: 6, md: 10 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <PsychologyIcon sx={{ fontSize: 48, color: '#10b981' }} />
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: '#1a1a1a',
              }}
            >
              AI-Powered Intelligence
            </Typography>
          </Box>

          <Typography
            variant="h6"
            sx={{
              maxWidth: '900px',
              mb: 6,
              color: '#4b5563',
              lineHeight: 1.8,
              fontSize: { xs: '1rem', md: '1.15rem' },
            }}
          >
            CRUD Expert is the <Box component="span" sx={{ color: '#10b981', fontWeight: 600 }}>first AI-powered API testing platform</Box> that combines cutting-edge artificial intelligence with comprehensive testing capabilities. Our AI engine doesn't just automate testing—it intelligently analyzes your APIs and accelerates your entire testing workflow.
          </Typography>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              mb: 4,
              color: '#1a1a1a',
            }}
          >
            Smart AI Capabilities
          </Typography>

          <Grid container spacing={3} sx={{ mb: 6 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3, borderRadius: 2, bgcolor: '#f9fafb', height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
                  <Box component="span" sx={{ color: '#10b981' }}>•</Box> Suggests Multiple Data Sets
                </Typography>
                <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                  AI intelligently analyzes your API requests and automatically suggests multiple test data sets based on your request schema and requirements
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3, borderRadius: 2, bgcolor: '#f9fafb', height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
                  <Box component="span" sx={{ color: '#10b981' }}>•</Box> Generates Test Data Using AI
                </Typography>
                <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                  Create realistic, comprehensive test data automatically—just describe what you need, and AI generates positive and negative test cases
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3, borderRadius: 2, bgcolor: '#f9fafb', height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
                  <Box component="span" sx={{ color: '#10b981' }}>•</Box> Smart Assertion Suggestions
                </Typography>
                <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                  AI analyzes your API responses and suggests relevant assertions to validate your APIs automatically
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3, borderRadius: 2, bgcolor: '#f9fafb', height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
                  <Box component="span" sx={{ color: '#10b981' }}>•</Box> Intelligent Test Automation
                </Typography>
                <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                  AI helps you build comprehensive test cycles faster by understanding your API structure and suggesting optimal test configurations
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Paper
            elevation={0}
            sx={{
              p: 4,
              bgcolor: '#f0fdf4',
              borderLeft: '4px solid #10b981',
              borderRadius: 2,
              fontStyle: 'italic',
              color: '#4b5563',
              fontSize: '1.1rem',
              lineHeight: 1.8,
            }}
          >
            What used to take hours now takes minutes with AI assistance. Our intelligent engine learns from your API patterns to deliver increasingly accurate suggestions.
          </Paper>
        </Box>

        <Divider sx={{ my: 8 }} />

        {/* Section 3: Core Features */}
        <Box sx={{ py: { xs: 6, md: 10 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 6 }}>
            <RocketIcon sx={{ fontSize: 48, color: '#10b981' }} />
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: '#1a1a1a',
              }}
            >
              Core Features
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: '#1a1a1a',
                  }}
                >
                  Advanced API Request Builder
                </Typography>
                <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                  Full HTTP method support (GET, POST, PUT, DELETE, PATCH), flexible request body types, dynamic URL building, and comprehensive request history tracking.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: '#1a1a1a',
                  }}
                >
                  Collections Management
                </Typography>
                <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                  Organize requests hierarchically within collections, group collections under projects, and import from Swagger/OpenAPI specifications.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: '#1a1a1a',
                  }}
                >
                  Environment & Variables
                </Typography>
                <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                  Multiple environments for Development, Staging, Production with request-level, collection-level, and project-level variable scoping.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 8 }} />

        {/* Section 4: AI-Powered Data-Driven Testing */}
        <Box sx={{ py: { xs: 6, md: 10 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <TargetIcon sx={{ fontSize: 48, color: '#10b981' }} />
            <RocketIcon sx={{ fontSize: 48, color: '#10b981' }} />
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: '#1a1a1a',
              }}
            >
              AI-Powered Data-Driven Testing
            </Typography>
          </Box>

          <Typography
            variant="h6"
            sx={{
              maxWidth: '900px',
              mb: 6,
              color: '#4b5563',
              lineHeight: 1.8,
              fontSize: { xs: '1rem', md: '1.15rem' },
            }}
          >
            Experience the future of API testing with our revolutionary <Box component="span" sx={{ color: '#10b981', fontWeight: 600 }}>AI-powered data generation</Box> and intelligent test data management system.
          </Typography>

          {/* 4-Step Process */}
          <Grid container spacing={4} sx={{ mb: 6 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
                <Box
                  sx={{
                    minWidth: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: '#10b981',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PsychologyIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1a1a1a' }}>
                    AI Analyzes Your Request
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                    Our AI engine examines your request structure, schema, and requirements to understand your testing needs
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
                <Box
                  sx={{
                    minWidth: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: '#10b981',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <LightbulbIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1a1a1a' }}>
                    AI Suggests Data Sets
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                    AI automatically suggests multiple relevant test data sets based on your API specifications and best practices
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
                <Box
                  sx={{
                    minWidth: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: '#10b981',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <DescriptionIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1a1a1a' }}>
                    Generate with Natural Language
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                    Simply describe your test scenario in plain English, and AI creates comprehensive test data including positive cases, negative cases, and edge conditions
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
                <Box
                  sx={{
                    minWidth: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: '#10b981',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PlayArrowIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1a1a1a' }}>
                    Execute & Validate
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                    Run parameterized tests with multiple data sets and automatically validate results against AI-suggested assertions
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Test Cases */}
          <Grid container spacing={4} sx={{ mt: 4 }}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  border: '1px solid #e5e7eb',
                  borderRadius: 2,
                  height: '100%',
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 3,
                    color: '#1a1a1a',
                  }}
                >
                  Positive Test Cases
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', mb: 3, lineHeight: 1.7 }}>
                  AI generates realistic, valid data that should pass validation:
                </Typography>
                <List dense>
                  <ListItem disablePadding sx={{ mb: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Schema-compliant data" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.95rem' } }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ mb: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Realistic values based on field types" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.95rem' } }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ mb: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Valid business logic scenarios" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.95rem' } }} />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Expected success paths" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.95rem' } }} />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  border: '1px solid #e5e7eb',
                  borderRadius: 2,
                  height: '100%',
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 3,
                    color: '#1a1a1a',
                  }}
                >
                  Negative Test Cases
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', mb: 3, lineHeight: 1.7 }}>
                  Comprehensive edge cases and invalid data:
                </Typography>
                <List dense>
                  <ListItem disablePadding sx={{ mb: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleIcon sx={{ color: '#ef4444', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Boundary conditions" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.95rem' } }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ mb: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleIcon sx={{ color: '#ef4444', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Invalid data types" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.95rem' } }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ mb: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleIcon sx={{ color: '#ef4444', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Missing required fields" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.95rem' } }} />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleIcon sx={{ color: '#ef4444', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Error handling scenarios" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.95rem' } }} />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 8 }} />

        {/* Section 5: AI-Powered Assertion Engine */}
        <Box sx={{ py: { xs: 6, md: 10 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: '#10b981' }} />
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: '#1a1a1a',
              }}
            >
              AI-Powered Assertion Engine
            </Typography>
          </Box>

          <Typography
            variant="h6"
            sx={{
              maxWidth: '900px',
              mb: 6,
              color: '#4b5563',
              lineHeight: 1.8,
              fontSize: { xs: '1rem', md: '1.15rem' },
            }}
          >
            Say goodbye to manual assertion creation. Our AI analyzes your API responses and intelligently suggests relevant assertions, saving hours of manual work and ensuring comprehensive test coverage.
          </Typography>

          {/* Feature Boxes */}
          <Grid container spacing={4} sx={{ mb: 6 }}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderLeft: '4px solid #10b981',
                  borderRadius: 2,
                  height: '100%',
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: '#1a1a1a',
                  }}
                >
                  Status Code Assertions
                </Typography>
                <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                  AI detects expected status codes based on response patterns and HTTP standards
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderLeft: '4px solid #10b981',
                  borderRadius: 2,
                  height: '100%',
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: '#1a1a1a',
                  }}
                >
                  JSON Path Assertions
                </Typography>
                <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                  AI identifies key fields (id, name, email, token, etc.) and suggests assertions automatically
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderLeft: '4px solid #10b981',
                  borderRadius: 2,
                  height: '100%',
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: '#1a1a1a',
                  }}
                >
                  Response Structure Analysis
                </Typography>
                <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                  AI understands your response schema and suggests appropriate validations for data types and formats
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderLeft: '4px solid #10b981',
                  borderRadius: 2,
                  height: '100%',
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: '#1a1a1a',
                  }}
                >
                  Bulk Suggestions
                </Typography>
                <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                  Get multiple assertion suggestions at once with one click—comprehensive coverage in seconds
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Testimonial */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              bgcolor: '#f0fdf4',
              borderLeft: '4px solid #10b981',
              borderRadius: 2,
              mb: 6,
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <FormatQuoteIcon sx={{ color: '#10b981', fontSize: 32, transform: 'rotate(180deg)' }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontStyle: 'italic',
                color: '#4b5563',
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.2rem' },
                fontWeight: 500,
              }}
            >
              "The AI-powered assertion suggestions have reduced our test creation time by 70%. What used to take hours now takes minutes."
            </Typography>
          </Paper>

          {/* Assertion Types */}
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                mb: 4,
                color: '#1a1a1a',
              }}
            >
              Multiple Assertion Types
            </Typography>
            <Grid container spacing={2}>
              {[
                'Status Code Validation',
                'JSON Path Assertions',
                'Response Time Validation',
                'Header Validation',
                'Body Content Validation',
                'Response Size Validation',
                'Content Type Validation',
                'Custom Script Validation',
                'Schema Validation',
              ].map((type, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: '#f9fafb',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <VerifiedUserIcon sx={{ color: '#10b981', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: '#4b5563' }}>
                      {type}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        <Divider sx={{ my: 8 }} />

        {/* Section 6: Automated Test Cycles */}
        <Box sx={{ py: { xs: 6, md: 10 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <AutorenewIcon sx={{ fontSize: 48, color: '#10b981' }} />
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: '#1a1a1a',
              }}
            >
              Automated Test Cycles
            </Typography>
          </Box>

          <Typography
            variant="h6"
            sx={{
              maxWidth: '900px',
              mb: 6,
              color: '#4b5563',
              lineHeight: 1.8,
              fontSize: { xs: '1rem', md: '1.15rem' },
            }}
          >
            Build comprehensive test suites by selecting collections, requests, data sets, and assertions. Execute tests in sequence or parallel, and track detailed results with historical data and visual status indicators.
          </Typography>

          {/* 4-Step Process Flow */}
          <Grid container spacing={4} sx={{ mb: 6 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative' }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    bgcolor: '#f9fafb',
                    borderRadius: 3,
                    border: '1px solid #e5e7eb',
                    height: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        minWidth: 56,
                        height: 56,
                        borderRadius: '50%',
                        bgcolor: '#10b981',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <FolderIcon sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: '#1a1a1a' }}>
                        Create Test Suite
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                        Select collections, requests, and data sets
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
                <Box
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    justifyContent: 'center',
                    my: 2,
                  }}
                >
                  <ArrowDownwardIcon sx={{ color: '#d1d5db', fontSize: 32 }} />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative' }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    bgcolor: '#f9fafb',
                    borderRadius: 3,
                    border: '1px solid #e5e7eb',
                    height: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        minWidth: 56,
                        height: 56,
                        borderRadius: '50%',
                        bgcolor: '#10b981',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <SettingsIcon sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: '#1a1a1a' }}>
                        Configure Execution
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                        Choose environment and execution order
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
                <Box
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    justifyContent: 'center',
                    my: 2,
                  }}
                >
                  <ArrowDownwardIcon sx={{ color: '#d1d5db', fontSize: 32 }} />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative' }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    bgcolor: '#f9fafb',
                    borderRadius: 3,
                    border: '1px solid #e5e7eb',
                    height: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        minWidth: 56,
                        height: 56,
                        borderRadius: '50%',
                        bgcolor: '#10b981',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PlayArrowIcon sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: '#1a1a1a' }}>
                        Execute Tests
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                        Run batch execution with real-time tracking
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
                <Box
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    justifyContent: 'center',
                    my: 2,
                  }}
                >
                  <ArrowDownwardIcon sx={{ color: '#d1d5db', fontSize: 32 }} />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  bgcolor: '#f9fafb',
                  borderRadius: 3,
                  border: '1px solid #e5e7eb',
                  height: '100%',
                }}
              >
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      minWidth: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: '#10b981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <AssessmentIcon sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: '#1a1a1a' }}>
                      Analyze Results
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                      Review hierarchical results with detailed feedback
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Visual Status Indicators */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              bgcolor: '#f0fdf4',
              borderRadius: 3,
              border: '1px solid #10b981',
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <CheckCircleIcon sx={{ color: '#10b981', fontSize: 32 }} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: '#1a1a1a',
                }}
              >
                Visual Status Indicators
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ color: '#4b5563', lineHeight: 1.8, fontSize: '1.05rem' }}>
              Color-coded status tracking at every level: Collection → Request → Data Set → Assertion. See exactly what passed, failed, or is still running with intuitive visual feedback.
            </Typography>
          </Paper>
        </Box>

        <Divider sx={{ my: 8 }} />

        {/* Section 7: Authentication & Security */}
        <Box sx={{ py: { xs: 6, md: 10 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 6 }}>
            <LockIcon sx={{ fontSize: 48, color: '#fbbf24' }} />
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: '#1a1a1a',
              }}
            >
              Authentication & Security
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: '#1a1a1a',
                }}
              >
                Multiple Auth Methods
              </Typography>
              <List>
                <ListItem disablePadding sx={{ mb: 2 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <VpnKeyIcon sx={{ color: '#10b981', fontSize: 24 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Bearer Token"
                    secondary="JWT and OAuth token support"
                    sx={{
                      '& .MuiListItemText-primary': { color: '#1a1a1a', fontWeight: 600 },
                      '& .MuiListItemText-secondary': { color: '#6b7280' },
                    }}
                  />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 2 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <LockIcon sx={{ color: '#10b981', fontSize: 24 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Basic Authentication"
                    secondary="Username/password authentication"
                    sx={{
                      '& .MuiListItemText-primary': { color: '#1a1a1a', fontWeight: 600 },
                      '& .MuiListItemText-secondary': { color: '#6b7280' },
                    }}
                  />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 2 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <VpnKeyIcon sx={{ color: '#10b981', fontSize: 24 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="API Key"
                    secondary="Custom API key authentication"
                    sx={{
                      '& .MuiListItemText-primary': { color: '#1a1a1a', fontWeight: 600 },
                      '& .MuiListItemText-secondary': { color: '#6b7280' },
                    }}
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <SecurityIcon sx={{ color: '#10b981', fontSize: 24 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Custom Auth"
                    secondary="Support for custom authentication schemes"
                    sx={{
                      '& .MuiListItemText-primary': { color: '#1a1a1a', fontWeight: 600 },
                      '& .MuiListItemText-secondary': { color: '#6b7280' },
                    }}
                  />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  bgcolor: '#f0fdf4',
                  borderRadius: 3,
                  border: '1px solid #10b981',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: '#4b5563',
                    lineHeight: 1.8,
                    fontSize: '1.1rem',
                  }}
                >
                  All credentials are stored with <Box component="span" sx={{ color: '#10b981', fontWeight: 600 }}>end-to-end encryption</Box>, and authentication can be configured per environment for maximum flexibility.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 8 }} />

        {/* Section 8: Multi-Tenancy & Enterprise Features */}
        <Box sx={{ py: { xs: 6, md: 10 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <BusinessIcon sx={{ fontSize: 48, color: '#10b981' }} />
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: '#1a1a1a',
              }}
            >
              Multi-Tenancy & Enterprise Features
            </Typography>
          </Box>

          <Typography
            variant="h6"
            sx={{
              maxWidth: '900px',
              mb: 6,
              color: '#4b5563',
              lineHeight: 1.8,
              fontSize: { xs: '1rem', md: '1.15rem' },
            }}
          >
            Built from the ground up with enterprise-grade multi-tenancy architecture, ensuring complete data isolation and security for organizations of all sizes.
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    bgcolor: '#10b981',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <DomainIcon sx={{ color: 'white', fontSize: 32 }} />
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: '#1a1a1a',
                  }}
                >
                  Tenant Isolation
                </Typography>
                <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                  Complete data isolation between organizations with schema-per-tenant architecture for enterprise plans and shared database with row-level security for standard plans
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    bgcolor: '#10b981',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <SecurityIcon sx={{ color: 'white', fontSize: 32 }} />
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: '#1a1a1a',
                  }}
                >
                  Secure by Design
                </Typography>
                <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                  Automatic tenant filtering on all queries, row-level security enforcement, and encrypted credential storage ensure your data stays protected
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    bgcolor: '#10b981',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <PublicIcon sx={{ color: 'white', fontSize: 32 }} />
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: '#1a1a1a',
                  }}
                >
                  Subdomain Access
                </Typography>
                <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                  Each organization gets a custom subdomain for branded access and clear tenant separation
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 8 }} />

        {/* Section 9: Swagger Integration */}
        <Box sx={{ py: { xs: 6, md: 10 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 6 }}>
            <IntegrationInstructionsIcon sx={{ fontSize: 48, color: '#10b981' }} />
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: '#1a1a1a',
              }}
            >
              Swagger Integration
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 4,
              bgcolor: '#f9fafb',
              borderRadius: 3,
              border: '1px solid #e5e7eb',
            }}
          >
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 3,
                    color: '#1a1a1a',
                  }}
                >
                  Import & Parse
                </Typography>
                <List dense>
                  <ListItem disablePadding sx={{ mb: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Import from Swagger/OpenAPI files or URLs" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.95rem' } }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ mb: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Support for OpenAPI 2.0 and 3.0 specifications" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.95rem' } }} />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Preview before import with detailed breakdown" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.95rem' } }} />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 3,
                    color: '#1a1a1a',
                  }}
                >
                  Automatic Generation
                </Typography>
                <List dense>
                  <ListItem disablePadding sx={{ mb: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Auto-create collections from API definitions" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.95rem' } }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ mb: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Convert all endpoints to testable requests" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.95rem' } }} />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Generate data sets with AI-powered test data" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.95rem' } }} />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 3,
                    color: '#1a1a1a',
                  }}
                >
                  Schema Preservation
                </Typography>
                <List dense>
                  <ListItem disablePadding sx={{ mb: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Maintain request and response schemas" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.95rem' } }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ mb: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Automatic environment detection and setup" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.95rem' } }} />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Schema-based validation for responses" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.95rem' } }} />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        <Divider sx={{ my: 8 }} />

        {/* Section 10: Advanced Capabilities */}
        <Box sx={{ py: { xs: 6, md: 10 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 6 }}>
            <BuildIcon sx={{ fontSize: 48, color: '#10b981' }} />
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: '#1a1a1a',
              }}
            >
              Advanced Capabilities
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  border: '1px solid #e5e7eb',
                  borderRadius: 3,
                  height: '100%',
                }}
              >
                <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
                  <IntegrationInstructionsIcon sx={{ color: '#10b981', fontSize: 32 }} />
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: '#1a1a1a',
                    }}
                  >
                    Swagger/OpenAPI Integration
                  </Typography>
                </Box>
                <List dense>
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText primary="Import complete API specifications" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.9rem' } }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText primary="Automatic collection creation" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.9rem' } }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText primary="Request generation from endpoints" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.9rem' } }} />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText primary="Schema preservation for validation" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.9rem' } }} />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  border: '1px solid #e5e7eb',
                  borderRadius: 3,
                  height: '100%',
                }}
              >
                <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
                  <AnalyticsIcon sx={{ color: '#10b981', fontSize: 32 }} />
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: '#1a1a1a',
                    }}
                  >
                    Response Analysis
                  </Typography>
                </Box>
                <List dense>
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText primary="JSON syntax highlighting" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.9rem' } }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText primary="XML formatting" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.9rem' } }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText primary="Response time tracking" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.9rem' } }} />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText primary="Response schema validation" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.9rem' } }} />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  border: '1px solid #e5e7eb',
                  borderRadius: 3,
                  height: '100%',
                }}
              >
                <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
                  <CloudQueueIcon sx={{ color: '#10b981', fontSize: 32 }} />
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: '#1a1a1a',
                    }}
                  >
                    Proxy Server & CORS
                  </Typography>
                </Box>
                <List dense>
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText primary="Built-in proxy server for API requests" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.9rem' } }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText primary="CORS bypass for cross-origin requests" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.9rem' } }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText primary="Optional browser extension" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.9rem' } }} />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText primary="Transparent request forwarding" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.9rem' } }} />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  border: '1px solid #e5e7eb',
                  borderRadius: 3,
                  height: '100%',
                }}
              >
                <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
                  <CodeIcon sx={{ color: '#10b981', fontSize: 32 }} />
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: '#1a1a1a',
                    }}
                  >
                    Advanced Testing
                  </Typography>
                </Box>
                <List dense>
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText primary="Variable substitution and extraction" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.9rem' } }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText primary="Request chaining" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.9rem' } }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText primary="Conditional logic" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.9rem' } }} />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText primary="Parallel execution" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '0.9rem' } }} />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 8 }} />

        {/* Section 11: User Experience */}
        <Box sx={{ py: { xs: 6, md: 10 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <PaletteIcon sx={{ fontSize: 48, color: '#10b981' }} />
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: '#1a1a1a',
              }}
            >
              User Experience
            </Typography>
          </Box>

          <Typography
            variant="h6"
            sx={{
              maxWidth: '900px',
              mb: 6,
              color: '#4b5563',
              lineHeight: 1.8,
              fontSize: { xs: '1rem', md: '1.15rem' },
            }}
          >
            Built with <Box component="span" sx={{ color: '#10b981', fontWeight: 600 }}>modern Material-UI design</Box> for an intuitive, powerful interface that works seamlessly across all devices.
          </Typography>

          <Grid container spacing={3}>
            {[
              {
                icon: <RocketIcon sx={{ fontSize: 28 }} />,
                title: 'Responsive Design',
                description: 'Works seamlessly on desktop, tablet, and mobile devices',
              },
              {
                icon: <DarkModeIcon sx={{ fontSize: 28 }} />,
                title: 'Dark Mode Support',
                description: 'Eye-friendly dark theme option for extended use',
              },
              {
                icon: <UpdateIcon sx={{ fontSize: 28 }} />,
                title: 'Real-Time Updates',
                description: 'Live updates during request execution with visual feedback',
              },
              {
                icon: <DragIndicatorIcon sx={{ fontSize: 28 }} />,
                title: 'Drag & Drop',
                description: 'Intuitive drag-and-drop for organizing requests and collections',
              },
              {
                icon: <KeyboardIcon sx={{ fontSize: 28 }} />,
                title: 'Keyboard Shortcuts',
                description: 'Power user keyboard shortcuts for faster workflow',
              },
              {
                icon: <SearchIcon sx={{ fontSize: 28 }} />,
                title: 'Search & Filter',
                description: 'Quick search and filtering across collections and requests',
              },
            ].map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    border: '1px solid #e5e7eb',
                    borderRadius: 2,
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        bgcolor: '#10b981',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Box sx={{ color: 'white' }}>{feature.icon}</Box>
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: '#1a1a1a',
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.6 }}>
                        {feature.description}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 8 }} />

        {/* Section 12: Reporting & Analytics */}
        <Box sx={{ py: { xs: 6, md: 10 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 6 }}>
            <BarChartIcon sx={{ fontSize: 48, color: '#10b981' }} />
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: '#1a1a1a',
              }}
            >
              Reporting & Analytics
            </Typography>
          </Box>

          {/* KPIs */}
          <Grid container spacing={4} sx={{ mb: 8 }}>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  bgcolor: '#f0fdf4',
                  borderRadius: 3,
                  border: '1px solid #10b981',
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    color: '#10b981',
                    mb: 1,
                  }}
                >
                  99.9%
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    color: '#1a1a1a',
                  }}
                >
                  Platform Uptime
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  Enterprise-grade reliability
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  bgcolor: '#f0fdf4',
                  borderRadius: 3,
                  border: '1px solid #10b981',
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    color: '#10b981',
                    mb: 1,
                  }}
                >
                  70%
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    color: '#1a1a1a',
                  }}
                >
                  Time Saved
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  With AI-powered features
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  bgcolor: '#f0fdf4',
                  borderRadius: 3,
                  border: '1px solid #10b981',
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    color: '#10b981',
                    mb: 1,
                  }}
                >
                  10x
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    color: '#1a1a1a',
                  }}
                >
                  Faster Testing
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  Compared to manual testing
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Comprehensive Reports */}
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  mb: 4,
                  color: '#1a1a1a',
                }}
              >
                Comprehensive Reports
              </Typography>
              <List>
                <ListItem disablePadding sx={{ mb: 2 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckCircleIcon sx={{ color: '#10b981', fontSize: 24 }} />
                  </ListItemIcon>
                  <ListItemText primary="Detailed test execution reports" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '1rem' } }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 2 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckCircleIcon sx={{ color: '#10b981', fontSize: 24 }} />
                  </ListItemIcon>
                  <ListItemText primary="Success/failure rate tracking" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '1rem' } }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 2 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckCircleIcon sx={{ color: '#10b981', fontSize: 24 }} />
                  </ListItemIcon>
                  <ListItemText primary="Performance metrics and analytics" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '1rem' } }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 2 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckCircleIcon sx={{ color: '#10b981', fontSize: 24 }} />
                  </ListItemIcon>
                  <ListItemText primary="Historical trend analysis" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '1rem' } }} />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckCircleIcon sx={{ color: '#10b981', fontSize: 24 }} />
                  </ListItemIcon>
                  <ListItemText primary="Export capabilities (PDF, CSV)" sx={{ '& .MuiListItemText-primary': { color: '#4b5563', fontSize: '1rem' } }} />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  bgcolor: '#f9fafb',
                  borderRadius: 3,
                  border: '1px solid #e5e7eb',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <TrendingUpIcon sx={{ fontSize: 80, color: '#10b981', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#6b7280' }}>
                    Real-time analytics dashboard
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 8 }} />

        {/* Section 13: Integration & Extensibility */}
        <Box sx={{ py: { xs: 6, md: 10 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <PowerIcon sx={{ fontSize: 48, color: '#10b981' }} />
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: '#1a1a1a',
              }}
            >
              Integration & Extensibility
            </Typography>
          </Box>

          <Typography
            variant="h6"
            sx={{
              maxWidth: '900px',
              mb: 6,
              color: '#4b5563',
              lineHeight: 1.8,
              fontSize: { xs: '1rem', md: '1.15rem' },
            }}
          >
            Connect CRUD Expert with your existing development workflow and tools for seamless automation and continuous testing.
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  border: '1px solid #e5e7eb',
                  borderRadius: 3,
                  height: '100%',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    bgcolor: '#f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <ApiIcon sx={{ color: '#1a1a1a', fontSize: 32 }} />
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: '#1a1a1a',
                  }}
                >
                  RESTful API
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.6 }}>
                  Full REST API for programmatic access and custom integrations
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  border: '1px solid #e5e7eb',
                  borderRadius: 3,
                  height: '100%',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    bgcolor: '#f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <WebhookIcon sx={{ color: '#1a1a1a', fontSize: 32 }} />
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: '#1a1a1a',
                  }}
                >
                  Webhook Support
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.6 }}>
                  Trigger webhooks on test completion for automated workflows
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  border: '1px solid #e5e7eb',
                  borderRadius: 3,
                  height: '100%',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    bgcolor: '#f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <CloudSyncIcon sx={{ color: '#1a1a1a', fontSize: 32 }} />
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: '#1a1a1a',
                  }}
                >
                  CI/CD Integration
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.6 }}>
                  Integrate with Jenkins, GitLab, GitHub Actions, and more
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  border: '1px solid #e5e7eb',
                  borderRadius: 3,
                  height: '100%',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    bgcolor: '#f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <CodeIcon sx={{ color: '#1a1a1a', fontSize: 32 }} />
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: '#1a1a1a',
                  }}
                >
                  Custom Scripts
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.6 }}>
                  Support for custom validation scripts and logic
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 8 }} />

        {/* Section 14: Security & Compliance */}
        <Box sx={{ py: { xs: 6, md: 10 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 6 }}>
            <LockIcon sx={{ fontSize: 48, color: '#fbbf24' }} />
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: '#1a1a1a',
              }}
            >
              Security & Compliance
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  bgcolor: '#fef3c7',
                  borderRadius: 3,
                  border: '1px solid #fbbf24',
                  height: '100%',
                }}
              >
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: '#10b981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <LockIcon sx={{ color: 'white', fontSize: 32 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        color: '#1a1a1a',
                      }}
                    >
                      Data Encryption
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#4b5563', lineHeight: 1.7 }}>
                      End-to-end encryption for data in transit and at rest using industry-standard algorithms
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  bgcolor: '#fef3c7',
                  borderRadius: 3,
                  border: '1px solid #fbbf24',
                  height: '100%',
                }}
              >
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: '#10b981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <SecurityIcon sx={{ color: 'white', fontSize: 32 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        color: '#1a1a1a',
                      }}
                    >
                      Access Control
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#4b5563', lineHeight: 1.7 }}>
                      Role-based access control with fine-grained permissions and automatic tenant isolation
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  bgcolor: '#fef3c7',
                  borderRadius: 3,
                  border: '1px solid #fbbf24',
                  height: '100%',
                }}
              >
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: '#10b981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <ArticleIcon sx={{ color: 'white', fontSize: 32 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        color: '#1a1a1a',
                      }}
                    >
                      Audit Logging
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#4b5563', lineHeight: 1.7 }}>
                      Comprehensive audit trails for all operations with compliance-ready reporting
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  bgcolor: '#fef3c7',
                  borderRadius: 3,
                  border: '1px solid #fbbf24',
                  height: '100%',
                }}
              >
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: '#10b981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <AssignmentIcon sx={{ color: 'white', fontSize: 32 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        color: '#1a1a1a',
                      }}
                    >
                      GDPR Compliant
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#4b5563', lineHeight: 1.7 }}>
                      Data privacy and compliance features meeting international standards
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 8 }} />

        {/* Final CTA */}
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 3,
              color: '#1a1a1a',
            }}
          >
            Ready to Transform Your API Testing?
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mb: 4,
              color: '#6b7280',
              maxWidth: '700px',
              mx: 'auto',
            }}
          >
            Get started with our Unlimited Free plan today and experience the power of AI-powered API testing with CRUD Expert!
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/signup')}
            sx={{
              backgroundColor: '#10b981',
              color: 'white',
              fontWeight: 600,
              px: 6,
              py: 1.5,
              fontSize: '1.1rem',
              textTransform: 'none',
              borderRadius: 2,
              '&:hover': {
                backgroundColor: '#059669',
              },
            }}
          >
            Get Started Today
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Why;
